export class EsploraError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EsploraError";
  }
}

export class EsploraClient {
  private readonly bases: string[];

  constructor(
    baseUrls: string[],
    private readonly timeoutMs: number = 15000,
  ) {
    if (baseUrls.length === 0) {
      throw new Error("at least one Esplora base URL required");
    }
    this.bases = baseUrls.map((u) => u.replace(/\/$/, ""));
  }

  private async tryFetch(path: string): Promise<Response> {
    let lastErr: unknown;
    for (const base of this.bases) {
      const url = `${base}${path}`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.timeoutMs);
      try {
        const resp = await fetch(url, { signal: controller.signal });
        clearTimeout(timer);
        if (resp.ok) return resp;
        lastErr = new Error(`${url} -> ${resp.status}`);
      } catch (e) {
        clearTimeout(timer);
        lastErr = e;
      }
    }
    throw new EsploraError(`all ${this.bases.length} Esplora sources failed: ${String(lastErr)}`);
  }

  async tipHeight(): Promise<number> {
    const resp = await this.tryFetch("/blocks/tip/height");
    return Number.parseInt((await resp.text()).trim(), 10);
  }

  async confirmationsForBlock(height: number): Promise<number> {
    const tip = await this.tipHeight();
    return Math.max(0, tip - height + 1);
  }

  async blockTimestamp(height: number): Promise<string> {
    const hashResp = await this.tryFetch(`/block-height/${height}`);
    const blockHash = (await hashResp.text()).trim();
    const blockResp = await this.tryFetch(`/block/${blockHash}`);
    const data = (await blockResp.json()) as { timestamp: number };
    const dt = new Date(data.timestamp * 1000);
    return dt.toISOString().replace(/\.\d{3}Z$/, ".000Z");
  }
}
